import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import * as devicesApi from "../../api/devices";
import * as anagraficaApi from "../../api/anagrafica";
import { CentroAssistenzaOption, OperatoreLogistico } from "../../api/anagrafica";
import * as clientsApi from "../../api/clients";
import { Cliente, SedeCliente } from "../../api/clients";
import {
  DeviceDettaglio,
  StatoDevice,
  TipoPossessoDevice,
  STATO_LABELS,
  TIPO_POSSESSO_LABELS,
  TIPI_DISPOSITIVO,
  TIPO_DISPOSITIVO_LABELS,
  TipoDispositivoDevice,
  TipoServizioPrecredit,
  SERVIZI_PRECREDIT,
  SERVIZIO_PRECREDIT_LABELS,
} from "../../api/devices";
import { getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { etichettaMatricola } from "../../utils/matricola";

const STATI: StatoDevice[] = ["attivo", "dismesso", "rubato"];
const POSSESSI: TipoPossessoDevice[] = ["venduto_evo", "venduto_terzi", "noleggio", "proprieta_cliente"];

/**
 * Valore sentinella della select sede: non è un id, è la richiesta di creare
 * una sede. Un id vuoto significa già "non collegata", quindi serviva un terzo
 * valore distinto da entrambi.
 */
const CREA_SEDE = "__crea__";

/** `<input type="date">` vuole YYYY-MM-DD; dal backend arriva un ISO completo. */
function perInputDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

interface FormValues {
  stato: StatoDevice;
  /** "" = non noto: è un valore ammesso, la colonna è nullable. */
  tipo_possesso: TipoPossessoDevice | "";
  /** "" = non classificato, che è lo stato di quasi tutto il parco storico. */
  tipo_dispositivo: TipoDispositivoDevice | "";
  /**
   * Tre valori e non una spunta: "non noto" è diverso da "no". Una casella
   * deselezionata direbbe che il device NON è stato installato da Evo, che per
   * il parco storico è un'affermazione che nessuno ha verificato.
   */
  installato_da_evo: "" | "si" | "no";
  centro_assistenza_installazione_id: string;
  data_installazione: string;
  data_attivazione: string;
  data_dismissione: string;
  data_consegna_prevista: string;
  /** Usata solo se il device non ha ancora una matricola: vedi il campo nel form. */
  matricola: string;
  note: string;
}

/**
 * I campi che appartengono alla relazione device↔cliente, non al device.
 * `nuovaSede` non è null solo mentre l'utente sta creando una sede da questo
 * form: contiene il nome che sta digitando, e al salvataggio diventa una
 * SedeCliente vera.
 */
interface CampiRelazione {
  sede_id: string;
  nuovaSede: string | null;
  codice_gdo: string;
  servizio: TipoServizioPrecredit | "";
  /** Conto tecnico della coppia cassaforte–cliente (issue #211, passo 3 di Stato lavori). */
  conto_tecnico: string;
  /** Operatori territoriali — compilabili solo sulle Casseforti. */
  trasporto_valori_id: string;
  sala_conta_id: string;
}

/** «Alfa», «Alfa e Beta», «Alfa, Beta e Gamma». */
function elencoNomi(nomi: string[]): string {
  return nomi.length <= 1 ? (nomi[0] ?? "") : `${nomi.slice(0, -1).join(", ")} e ${nomi[nomi.length - 1]}`;
}

/** boolean|null -> valore della select, e ritorno. */
function daBooleano(v: boolean | null): "" | "si" | "no" {
  return v === null ? "" : v ? "si" : "no";
}
function aBooleano(v: "" | "si" | "no"): boolean | null {
  return v === "" ? null : v === "si";
}

/**
 * I campi che appartengono alla relazione device↔cliente: sede, codice GDO,
 * servizio e conto tecnico.
 *
 * Estratto perché li usano DUE posti — i blocchi delle relazioni già esistenti
 * e quello del cliente che si sta aggiungendo — e due copie divergerebbero al
 * primo ritocco. La cosa che non deve divergere è la regola sulla sede: la voce
 * "Crea sede da questo cliente…" compare SOLO dove il cliente non ne ha
 * nessuna, perché dove ce ne sono va scelta quella giusta invece di creare un
 * doppione.
 */
function CampiDellaRelazione({
  nomeCliente,
  campi,
  sedi,
  operatori,
  eCassaforte,
  notaGdo,
  onChange,
}: {
  nomeCliente: string;
  campi: CampiRelazione;
  /**
   * Avviso sotto il codice GDO quando la cassaforte ha più clienti: il codice
   * è uno per cassaforte e cambiarlo qui lo cambia per tutti (PR G, regola nel
   * backend in `utils/codiceGdo.ts`). `null` con un cliente solo.
   */
  notaGdo: string | null;
  /** `undefined` finché le sedi del cliente non sono state caricate. */
  sedi: SedeCliente[] | undefined;
  /** Anagrafica operatori logistici, gia' caricata dal genitore. */
  operatori: OperatoreLogistico[];
  /**
   * Trasporto valori e sala conta riguardano SOLO le casseforti: sugli altri
   * tipi i due campi non compaiono. Il vincolo non e' esprimibile a database
   * (e' cross-tabella) e vive qui e nell'API — vedi il commento su
   * `device_cliente` nello schema.
   */
  eCassaforte: boolean;
  onChange: (patch: Partial<CampiRelazione>) => void;
}) {
  const sediCaricate = sedi !== undefined;
  const senzaSedi = sediCaricate && sedi.length === 0;
  const staCreando = campi.nuovaSede !== null;

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-ink/60">Sede</label>

          {staCreando ? (
            // Il nome arriva precompilato con la ragione sociale ma resta
            // modificabile: l'insegna del punto vendita spesso non coincide, e
            // nessuno dei clienti senza sedi ha l'insegna valorizzata in
            // anagrafica da cui pescarla.
            <>
              <Input
                aria-label={`Nome della nuova sede — ${nomeCliente}`}
                value={campi.nuovaSede ?? ""}
                onChange={(e) => onChange({ nuovaSede: e.target.value })}
                placeholder="Nome del punto vendita"
              />
              <button
                type="button"
                onClick={() => onChange({ nuovaSede: null })}
                className="self-start text-xs text-ink/50 hover:text-ink hover:underline"
              >
                Annulla creazione
              </button>
            </>
          ) : (
            <select
              aria-label={`Sede — ${nomeCliente}`}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              value={campi.sede_id}
              disabled={!sediCaricate}
              onChange={(e) => {
                if (e.target.value === CREA_SEDE) {
                  onChange({ sede_id: "", nuovaSede: nomeCliente });
                } else {
                  onChange({ sede_id: e.target.value });
                }
              }}
            >
              <option value="">{sediCaricate ? "Non collegata" : "Caricamento…"}</option>
              {(sedi ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.nome_sede ?? "Sede senza nome", s.citta].filter(Boolean).join(" · ")}
                </option>
              ))}
              {/* Offerta solo quando il cliente non ha proprio sedi: dove ce ne
                  sono, va scelta quella giusta, non creato un doppione. */}
              {senzaSedi && <option value={CREA_SEDE}>Crea sede da questo cliente…</option>}
            </select>
          )}

          {senzaSedi && !staCreando && (
            <p className="text-xs text-ink/45">Questo cliente non ha sedi in anagrafica.</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-ink/60">Codice GDO</label>
          <Input
            aria-label={`Codice GDO — ${nomeCliente}`}
            value={campi.codice_gdo}
            onChange={(e) => onChange({ codice_gdo: e.target.value })}
            placeholder="es. GDO19"
          />
          {notaGdo && <p className="text-xs text-ink/45">{notaGdo}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-ink/60">Servizio precredit</label>
          <select
            aria-label={`Servizio precredit — ${nomeCliente}`}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            value={campi.servizio}
            onChange={(e) => onChange({ servizio: e.target.value as TipoServizioPrecredit | "" })}
          >
            {/* Vuoto = non si sa. "Nessun Collegamento Banca" è invece un'assenza
                verificata, ed è una voce dell'elenco. */}
            <option value="">Non specificato</option>
            {SERVIZI_PRECREDIT.map((s) => (
              <option key={s} value={s}>
                {SERVIZIO_PRECREDIT_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        {/* Per ogni cliente il suo: su una cassaforte condivisa ognuno versa sul
            proprio conto tecnico. Visibile su tutti i tipi, come il servizio:
            quasi tutto il parco è ancora senza tipo. */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-ink/60">Conto tecnico</label>
          <Input
            aria-label={`Conto tecnico — ${nomeCliente}`}
            value={campi.conto_tecnico}
            onChange={(e) => onChange({ conto_tecnico: e.target.value })}
            maxLength={50}
            placeholder="Numero del conto tecnico"
          />
        </div>
      </div>

      {/* Gli operatori territoriali del punto. Solo casseforti: su un
          rendiresto o una TVCC non c'e' contante da ritirare e contare, e
          mostrarli farebbe sembrare che il dato manchi invece che non
          esistere. Le due liste vengono dalla stessa anagrafica, filtrate
          sulle rispettive spunte. */}
      {eCassaforte && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectOperatore
            etichetta="Trasporto valori"
            nomeCliente={nomeCliente}
            valore={campi.trasporto_valori_id}
            opzioni={operatori.filter((o) => o.fa_trasporto_valori)}
            onChange={(v) => onChange({ trasporto_valori_id: v })}
          />
          <SelectOperatore
            etichetta="Sala conta"
            nomeCliente={nomeCliente}
            valore={campi.sala_conta_id}
            opzioni={operatori.filter((o) => o.fa_sala_conta)}
            onChange={(v) => onChange({ sala_conta_id: v })}
          />
        </div>
      )}
    </>
  );
}

/**
 * Una delle due select degli operatori. Estratta perche' sono identiche in
 * tutto tranne l'elenco e l'etichetta, e due copie divergerebbero al primo
 * ritocco — stesso motivo per cui esiste `CampiDellaRelazione`.
 *
 * Se l'operatore attualmente salvato non e' piu' in elenco (gli hanno tolto la
 * spunta del mestiere dall'anagrafica) viene comunque mostrato, marcato: farlo
 * sparire dalla select cambierebbe in silenzio un dato che nessuno ha toccato,
 * e al primo salvataggio lo perderebbe davvero.
 */
function SelectOperatore({
  etichetta,
  nomeCliente,
  valore,
  opzioni,
  onChange,
}: {
  etichetta: string;
  nomeCliente: string;
  valore: string;
  opzioni: OperatoreLogistico[];
  onChange: (valore: string) => void;
}) {
  const fuoriElenco = valore !== "" && !opzioni.some((o) => o.id === valore);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-ink/60">{etichetta}</label>
      <select
        aria-label={`${etichetta} — ${nomeCliente}`}
        className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
        value={valore}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Non specificato</option>
        {opzioni.map((o) => (
          <option key={o.id} value={o.id}>
            {o.nome}
            {o.zona ? ` — ${o.zona}` : ""}
          </option>
        ))}
        {fuoriElenco && <option value={valore}>Operatore salvato, non più in questo elenco</option>}
      </select>
    </div>
  );
}

/**
 * Modifica dei dati anagrafici di un device.
 *
 * Non c'è la matricola: è l'identificativo fisico stampigliato sulla macchina e
 * tutto lo storico ci punta — la route la rifiuta comunque. Cliente e sede delle
 * relazioni ESISTENTI si modificano qui, ma spostare un device altrove resta
 * un'altra operazione: si chiude una relazione e se ne apre una nuova, non si
 * riscrive un campo. Aggiungere un cliente IN PIÙ, invece, si fa da qui.
 */
export function DeviceModifica({
  open,
  onClose,
  device,
  onSaved,
  onEliminato,
}: {
  open: boolean;
  onClose: () => void;
  device: DeviceDettaglio;
  onSaved: () => void;
  /**
   * Chiamata dopo un'eliminazione riuscita. Separata da `onSaved` perché il
   * device non esiste più: ricaricarne la scheda darebbe un 404, e chi chiama
   * deve portare l'utente altrove.
   */
  onEliminato?: () => void;
}) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Eliminazione definitiva.
   *
   * Il backend rifiuta con 409 quando c'è storico collegato, e il corpo porta i
   * conteggi per tipo. Riportarli è il punto: "impossibile eliminare" da solo
   * lascia l'utente a indovinare cosa staccare, mentre "3 interventi, 1
   * installazione" dice dove andare. E siccome quasi sempre la risposta giusta
   * non è staccare niente ma dismettere il device, il messaggio lo suggerisce —
   * come fa già il testo dell'errore lato server.
   */
  async function elimina() {
    const conferma = window.confirm(
      `Eliminare DEFINITIVAMENTE il device ${etichettaMatricola(device.matricola)}?\n\n` +
        "L'operazione non è reversibile. Se ha storico collegato verrà bloccata."
    );
    if (!conferma) return;

    setIsDeleting(true);
    try {
      await devicesApi.deleteDevice(device.id);
      showToast(`Device ${etichettaMatricola(device.matricola)} eliminato.`, "success");
      onClose();
      onEliminato?.();
    } catch (err) {
      const dettaglio = (err as { response?: { data?: { dettaglio?: Partial<devicesApi.DeviceCollegati> } } }).response
        ?.data?.dettaglio;
      if (dettaglio) {
        const parti = (Object.keys(dettaglio) as (keyof devicesApi.DeviceCollegati)[])
          .filter((k) => (dettaglio[k] ?? 0) > 0)
          .map((k) => devicesApi.etichettaCollegati(k, dettaglio[k] ?? 0));
        showToast(
          `Impossibile eliminare ${etichettaMatricola(device.matricola)}: ha ${parti.join(", ")}. ` +
            "Se non è più in uso, impostalo come dismesso invece di eliminarlo.",
          "error"
        );
      } else {
        showToast(getErrorMessage(err, "Impossibile eliminare il device."), "error");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  // Il catalogo si sceglie con l'autocomplete, che lavora su testo: l'id vero
  // viaggia a parte. Svuotare il campo significa scollegare il modello, che è
  // un'azione legittima (una riconciliazione sbagliata va potuta annullare).
  const [catalogoTesto, setCatalogoTesto] = useState(
    device.dispositivo_catalogo ? `${device.dispositivo_catalogo.marca} ${device.dispositivo_catalogo.modello}` : ""
  );
  const [catalogoId, setCatalogoId] = useState<string | null>(device.dispositivo_catalogo?.id ?? null);

  // POSIZIONE FISICA DEL DEVICE — non la sede di una relazione.
  //
  // Il testo digitato non finisce da nessuna parte: serve solo a trovare la
  // sede, e la sede scelta si mostra come chip. Da qui `soloSelezione`.
  const [sedeFisicaTesto, setSedeFisicaTesto] = useState(device.sede_cliente?.nome_sede ?? "");
  const [sedeFisicaId, setSedeFisicaId] = useState(device.sede_id ?? "");

  // Il servizio di precredito appartiene alla relazione device↔cliente, non al
  // device: si modifica una voce per ogni relazione in corso. Con una sola
  // relazione si legge come un campo normale; con due — le casseforti
  // condivise — restano due campi distinti, che è l'unica lettura onesta.
  const correnti = device.clienti.filter((c) => c.data_fine === null);

  /**
   * Un blocco di campi per ogni relazione in corso: sede, codice GDO e
   * servizio appartengono tutti alla coppia device↔cliente, non al device.
   * Con un cliente solo si legge come una sezione normale; con due — le
   * casseforti condivise — restano due blocchi distinti, che è l'unica
   * lettura onesta.
   */
  const [relazioni, setRelazioni] = useState<Record<string, CampiRelazione>>(() =>
    Object.fromEntries(
      correnti.map((c) => [
        c.id,
        {
          sede_id: c.sede_cliente?.id ?? "",
          nuovaSede: null,
          codice_gdo: c.codice_gdo ?? "",
          servizio: (c.tipo_servizio_precredit ?? "") as TipoServizioPrecredit | "",
          conto_tecnico: c.conto_tecnico ?? "",
          trasporto_valori_id: c.trasporto_valori_id ?? "",
          sala_conta_id: c.sala_conta_id ?? "",
        },
      ])
    )
  );

  /** Sedi disponibili per cliente, caricate una volta all'apertura del form. */
  const [sediPerCliente, setSediPerCliente] = useState<Record<string, SedeCliente[]>>({});

  useEffect(() => {
    const clientiUnici = [...new Set(correnti.map((c) => c.cliente.id))];
    Promise.all(
      clientiUnici.map((id) =>
        clientsApi
          .listSediByCliente(id)
          .then(({ sedi }) => [id, sedi] as const)
          // Un cliente le cui sedi non si caricano non deve bloccare il form:
          // resta senza opzioni, esattamente come uno che non ne ha.
          .catch(() => [id, [] as SedeCliente[]] as const)
      )
    ).then((coppie) => setSediPerCliente(Object.fromEntries(coppie)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Il cliente che si sta aggiungendo, se se ne sta aggiungendo uno. `null`
   * quando il blocco è chiuso: uno stato assente e non un oggetto vuoto, così
   * "sto aggiungendo un cliente" e "il campo cliente è vuoto" restano due cose
   * distinte anche al salvataggio.
   */
  const [nuovo, setNuovo] = useState<
    ({ clienteTesto: string; cliente_id: string; cliente_nome: string } & CampiRelazione) | null
  >(null);

  /**
   * Le sedi del cliente appena scelto vanno caricate adesso: quelle degli altri
   * sono state prese all'apertura del form, e questo cliente allora non c'era.
   * Finiscono nella stessa mappa, così la select si comporta identica.
   */
  function scegliCliente(cliente: Cliente) {
    setNuovo((n) =>
      n ? { ...n, cliente_id: cliente.id, cliente_nome: cliente.nome, clienteTesto: cliente.nome, sede_id: "", nuovaSede: null } : n
    );
    if (sediPerCliente[cliente.id] === undefined) {
      clientsApi
        .listSediByCliente(cliente.id)
        .then(({ sedi }) => setSediPerCliente((m) => ({ ...m, [cliente.id]: sedi })))
        // Stesso trattamento degli altri clienti: senza opzioni, mai bloccante.
        .catch(() => setSediPerCliente((m) => ({ ...m, [cliente.id]: [] })));
    }
  }

  function aggiornaRelazione(id: string, patch: Partial<CampiRelazione>) {
    const { codice_gdo, ...resto } = patch;
    setRelazioni((r) => ({ ...r, [id]: { ...r[id], ...resto } }));
    if (codice_gdo !== undefined) cambiaCodiceGdo(codice_gdo);
  }

  /**
   * Il codice GDO è UNO PER CASSAFORTE (PR G): scriverlo in un blocco lo scrive
   * in tutti, compreso il cliente che si sta aggiungendo. Lasciare i blocchi
   * indipendenti farebbe salvare codici diversi uno dopo l'altro, e l'ultimo
   * vincerebbe in silenzio: il backend allinea comunque tutte le relazioni
   * aperte al codice scritto (`utils/codiceGdo.ts`).
   */
  function cambiaCodiceGdo(valore: string) {
    setRelazioni((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, { ...v, codice_gdo: valore }])));
    setNuovo((n) => (n ? { ...n, codice_gdo: valore } : n));
  }

  // I codici GDO oggi nel form, distinti. Più di uno solo su un dato vecchio,
  // da prima della regola: lo si dice, perché salvarne uno vale per tutti.
  const codiciNelForm = [...new Set(Object.values(relazioni).map((c) => c.codice_gdo.trim()).filter(Boolean))];

  // I clienti che il codice GDO riguarda: quelli già collegati più quello che
  // si sta aggiungendo, se è stato scelto.
  const clientiCassaforte = [...correnti.map((c) => c.cliente.nome), ...(nuovo?.cliente_id ? [nuovo.cliente_nome] : [])];
  const notaGdo =
    clientiCassaforte.length < 2
      ? null
      : codiciNelForm.length > 1
        ? `I clienti hanno oggi codici GDO diversi (${codiciNelForm.join(", ")}): quello che salvi varrà per tutti.`
        : `Il codice GDO è uno per cassaforte: vale per ${elencoNomi(clientiCassaforte)}.`;

  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);
  useEffect(() => {
    // Fallisce in silenzio per i ruoli senza accesso all'anagrafica: la select
    // resta con la sola opzione "Non noto" invece di bloccare tutto il form.
    anagraficaApi
      .listCentriAssistenza()
      .then(({ centri: list }) => setCentri(list))
      .catch(() => setCentri([]));
  }, []);

  // Anagrafica operatori logistici: una volta sola, e l'elenco intero. Le due
  // select filtrano poi sulle spunte — chiamare l'API due volte con
  // `?capacita=` darebbe le stesse righe in due richieste.
  //
  // Se non si carica non si blocca il form: le select restano vuote e il resto
  // del device si salva comunque, come gia' fa la lista dei centri qui sopra.
  const [operatori, setOperatori] = useState<OperatoreLogistico[]>([]);
  useEffect(() => {
    anagraficaApi
      .listOperatoriLogistici()
      .then(({ operatori: list }) => setOperatori(list))
      .catch(() => setOperatori([]));
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      stato: device.stato,
      tipo_possesso: device.tipo_possesso ?? "",
      tipo_dispositivo: device.tipo_dispositivo ?? "",
      installato_da_evo: daBooleano(device.installato_da_evo),
      centro_assistenza_installazione_id: device.centro_assistenza_installazione?.id ?? "",
      data_installazione: perInputDate(device.data_installazione),
      data_attivazione: perInputDate(device.data_attivazione),
      data_dismissione: perInputDate(device.data_dismissione),
      data_consegna_prevista: perInputDate(device.data_consegna_prevista),
      matricola: "",
      note: device.note ?? "",
    },
  });

  // Reattivo alla select del tipo, non al valore salvato: cambiando tipo in
  // "Cassaforte" i due campi degli operatori devono comparire subito, senza
  // dover salvare e riaprire.
  const eCassaforte = watch("tipo_dispositivo") === "Cassaforte";

  const statoScelto = watch("stato");

  async function onSubmit(values: FormValues) {
    try {
      await devicesApi.updateDevice(device.id, {
        stato: values.stato,
        // "" non è un valore dell'enum: sul campo nullable diventa null.
        tipo_possesso: values.tipo_possesso === "" ? null : values.tipo_possesso,
        tipo_dispositivo: values.tipo_dispositivo === "" ? null : values.tipo_dispositivo,
        installato_da_evo: aBooleano(values.installato_da_evo),
        centro_assistenza_installazione_id: values.centro_assistenza_installazione_id || null,
        data_installazione: values.data_installazione || null,
        data_attivazione: values.data_attivazione || null,
        // Un device tornato attivo non ha una data di dismissione: azzerarla
        // qui esplicitamente evita che resti quella vecchia, invisibile nel
        // form perché il campo è disabilitato ma ancora nello stato.
        data_dismissione: values.stato === "attivo" ? null : values.data_dismissione || null,
        dispositivo_catalogo_id: catalogoTesto.trim() === "" ? null : catalogoId,
        // Stringa vuota = posizione non indicata, che e' uno stato legittimo:
        // 90 device su 164 non hanno una sede da cui ereditarla.
        sede_id: sedeFisicaId || null,
        data_consegna_prevista: values.data_consegna_prevista || null,
        // Solo quando manca, e solo se è stata scritta: da NULL a valore si
        // può, dopo il backend la rifiuta (409). Vuota = resta non nota.
        ...(device.matricola === null && values.matricola.trim() ? { matricola: values.matricola.trim() } : {}),
        note: values.note.trim() === "" ? null : values.note,
      });

      // Solo le relazioni davvero cambiate: una PATCH per riga toccata, non
      // una per riga esistente.
      //
      // In sequenza e non in parallelo, perché una relazione può dover creare
      // prima la sua sede: con Promise.all due relazioni dello stesso cliente
      // senza sedi ne creerebbero due uguali nello stesso istante.
      for (const c of correnti) {
        const campi = relazioni[c.id];
        if (!campi) continue;

        let sedeId: string | null = campi.sede_id || null;

        // Sede creata da qui: succede solo per i clienti che in anagrafica non
        // ne hanno nessuna. Il nome è quello confermato dall'utente, non la
        // ragione sociale copiata di nascosto — l'insegna del punto vendita
        // spesso non coincide, e una sede battezzata male resta sbagliata per
        // sempre in mappa e nella Vista Sede.
        if (campi.nuovaSede !== null) {
          const nome = campi.nuovaSede.trim();
          if (!nome) {
            showToast("Indica un nome per la nuova sede, o scegline una esistente.", "error");
            return;
          }
          const { sede } = await clientsApi.createSede({ cliente_id: c.cliente.id, nome_sede: nome });
          sedeId = sede.id;

          // La sede creata va scritta subito nello stato, prima di qualunque
          // altra chiamata. Senza, un errore più avanti nel salvataggio (la
          // PATCH qui sotto, o il nome vuoto di un'altra relazione) lascerebbe
          // `nuovaSede` valorizzato e `sede_id` vuoto: l'utente corregge,
          // risalva, e questa relazione crea una SECONDA sede con lo stesso
          // nome. Il salvataggio in sequenza evita due creazioni simultanee,
          // non due creazioni in due salvataggi successivi — che è il caso che
          // capita davvero, perché richiede solo un errore e un ritentativo.
          aggiornaRelazione(c.id, { sede_id: sede.id, nuovaSede: null });

          // E va aggiunta alle opzioni del cliente, altrimenti la select
          // tornerebbe a mostrare "Non collegata": l'elenco è caricato
          // all'apertura del form, e un id che non corrisponde a nessuna
          // `<option>` non si vede. Il dato in stato sarebbe giusto e quello a
          // schermo sbagliato — e da lì l'utente "corregge" ciò che è già
          // corretto. Come effetto, il cliente non è più senza sedi e la voce
          // "Crea sede…" sparisce da sola.
          setSediPerCliente((m) => ({ ...m, [c.cliente.id]: [...(m[c.cliente.id] ?? []), sede] }));
        }

        const patch: devicesApi.DeviceClientePatch = {};
        if (sedeId !== (c.sede_cliente?.id ?? null)) patch.sede_id = sedeId;
        if ((campi.codice_gdo.trim() || null) !== (c.codice_gdo ?? null)) {
          patch.codice_gdo = campi.codice_gdo.trim() || null;
        }
        if ((campi.servizio || null) !== (c.tipo_servizio_precredit ?? null)) {
          patch.tipo_servizio_precredit = campi.servizio || null;
        }
        if ((campi.conto_tecnico.trim() || null) !== (c.conto_tecnico ?? null)) {
          patch.conto_tecnico = campi.conto_tecnico.trim() || null;
        }
        // Solo per le casseforti, coerentemente con quello che il form mostra:
        // su un altro tipo i due campi non sono modificabili, quindi non
        // devono nemmeno partire — altrimenti cambiare tipo a un device
        // azzererebbe in silenzio operatori che nessuno ha toccato.
        if (eCassaforte) {
          if ((campi.trasporto_valori_id || null) !== (c.trasporto_valori_id ?? null)) {
            patch.trasporto_valori_id = campi.trasporto_valori_id || null;
          }
          if ((campi.sala_conta_id || null) !== (c.sala_conta_id ?? null)) {
            patch.sala_conta_id = campi.sala_conta_id || null;
          }
        }

        if (Object.keys(patch).length > 0) await devicesApi.updateDeviceCliente(c.id, patch);
      }

      // --- Cliente aggiunto ------------------------------------------------
      //
      // In coda alle relazioni esistenti e non in parallelo, per lo stesso
      // motivo del ciclo qui sopra: anche questa può dover creare prima la sua
      // sede, e due creazioni contemporanee dello stesso cliente ne farebbero
      // due uguali.
      if (nuovo) {
        if (!nuovo.cliente_id) {
          showToast("Scegli un cliente dall'elenco dei suggerimenti, o annulla l'aggiunta.", "error");
          return;
        }

        let sedeId: string | null = nuovo.sede_id || null;
        if (nuovo.nuovaSede !== null) {
          const nome = nuovo.nuovaSede.trim();
          if (!nome) {
            showToast("Indica un nome per la nuova sede, o scegline una esistente.", "error");
            return;
          }
          const { sede } = await clientsApi.createSede({ cliente_id: nuovo.cliente_id, nome_sede: nome });
          sedeId = sede.id;
          // Scritta subito nello stato, come nel ciclo sopra: se qualcosa
          // fallisce più avanti, un secondo salvataggio non ne crea un'altra.
          setNuovo((n) => (n ? { ...n, sede_id: sede.id, nuovaSede: null } : n));
          setSediPerCliente((m) => ({ ...m, [nuovo.cliente_id]: [...(m[nuovo.cliente_id] ?? []), sede] }));
        }

        await devicesApi.creaDeviceCliente({
          device_id: device.id,
          cliente_id: nuovo.cliente_id,
          sede_id: sedeId,
          codice_gdo: nuovo.codice_gdo.trim() || null,
          tipo_servizio_precredit: nuovo.servizio || null,
          conto_tecnico: nuovo.conto_tecnico.trim() || null,
          trasporto_valori_id: eCassaforte ? nuovo.trasporto_valori_id || null : null,
          sala_conta_id: eCassaforte ? nuovo.sala_conta_id || null : null,
        });
      }

      showToast("Dispositivo aggiornato.", "success");
      onClose();
      onSaved();
    } catch (err) {
      // Il 409 sul doppione di relazione ha un messaggio suo dal backend
      // ("Esiste già una relazione in corso tra questo device e questo
      // cliente"), che è più utile del generico: `getErrorMessage` lo usa
      // già se c'è. Qui si aggiunge solo il perché, che il backend non dice:
      // aprirne una seconda renderebbe ambiguo di chi è il device oggi.
      const risposta = (err as { response?: { status?: number; data?: { motivo?: string } } }).response;
      const stato = risposta?.status;
      // I 409 con un `motivo` (codice GDO diverso da quello della cassaforte,
      // relazione chiusa mentre la si modificava) hanno già un messaggio che
      // dice cosa fare: va mostrato così com'è, non scambiato per il doppione
      // qui sotto.
      if (stato === 409 && risposta?.data?.motivo) {
        showToast(getErrorMessage(err, "Il salvataggio è in conflitto con una modifica appena fatta."), "error");
      } else if (stato === 409 && nuovo?.cliente_nome) {
        showToast(
          `${nuovo.cliente_nome} è già collegato a questa cassaforte. ` +
            "Per cambiare sede o servizio modifica il blocco che c'è già, invece di aggiungerlo una seconda volta.",
          "error"
        );
      } else {
        showToast(getErrorMessage(err, "Impossibile aggiornare il dispositivo."), "error");
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Modifica ${etichettaMatricola(device.matricola)}`}
      size="md"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {/* Solo per chi ha il permesso: il backend rifiuta comunque gli altri
              ruoli, ma un pulsante che risponde sempre "non puoi" è peggio di un
              pulsante che non c'è. */}
          {hasPermission("devices:delete") ? (
            <Button type="button" variant="danger" onClick={elimina} loading={isDeleting}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Elimina
            </Button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" form="modifica-device-form" loading={isSubmitting}>
              Salva
            </Button>
          </div>
        </div>
      }
    >
      <form id="modifica-device-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {/* Solo per un pezzo entrato in magazzino senza seriale: la matricola
            si imposta una volta, poi non si tocca più (vedi PATCH /api/devices/:id). */}
        {device.matricola === null && (
          <Input
            label="Matricola"
            placeholder="Es. UB120004"
            hint="Non ancora nota. Una volta salvata non si cambia più: tutto lo storico ci punterà."
            {...register("matricola")}
          />
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="dev-stato" className="text-sm font-medium text-ink">
              Stato
            </label>
            <select
              id="dev-stato"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("stato")}
            >
              {STATI.map((s) => (
                <option key={s} value={s}>
                  {STATO_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="dev-possesso" className="text-sm font-medium text-ink">
              Tipo di possesso
            </label>
            <select
              id="dev-possesso"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("tipo_possesso")}
            >
              {/* "Non noto" è un valore vero, non un segnaposto: per gran parte
                  del parco storico il dato non esiste in nessuna fonte. */}
              <option value="">Non noto</option>
              {POSSESSI.map((p) => (
                <option key={p} value={p}>
                  {TIPO_POSSESSO_LABELS[p]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="dev-tipo" className="text-sm font-medium text-ink">
              Tipo di dispositivo
            </label>
            <select
              id="dev-tipo"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("tipo_dispositivo")}
            >
              {/* "Non classificato" è lo stato di quasi tutto il parco storico e
                  NON vuol dire "cassaforte": lasciarlo così è legittimo, e i non
                  classificati continuano a contare nelle statistiche per
                  servizio come hanno sempre fatto. */}
              <option value="">Non classificato</option>
              {TIPI_DISPOSITIVO.map((t) => (
                <option key={t} value={t}>
                  {TIPO_DISPOSITIVO_LABELS[t]}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink/45">
              Gli accessori (concentratori) restano fuori dalle statistiche per servizio: un servizio Pre Credit non
              possono averlo.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Data installazione" type="date" {...register("data_installazione")} />
          <Input label="Data attivazione" type="date" {...register("data_attivazione")} />
          <Input
            label="Data dismissione"
            type="date"
            // La data di dismissione ha senso solo per un device uscito di
            // servizio: lasciarla scrivibile su uno attivo produrrebbe una
            // riga che si contraddice da sola.
            disabled={statoScelto === "attivo"}
            hint={statoScelto === "attivo" ? "Solo per device dismessi o rubati" : undefined}
            {...register("data_dismissione")}
          />
        </div>

        <Input
          label="Consegna prevista"
          type="date"
          hint="Arrivo presunto in magazzino dal fornitore. Vuota = non nota."
          {...register("data_consegna_prevista")}
        />

        {/* DOVE STA IL PEZZO, e non la sede di una relazione: quelle si
            modificano piu' sotto, una per cliente. La distinzione conta sulle
            casseforti condivise, che sono l'unico posto in cui le due cose
            possono divergere — ed e' il motivo per cui questo campo esiste.

            Sta qui in alto, fra i dati del device, e non insieme ai blocchi
            per relazione: metterlo li' suggerirebbe che ce n'e' uno per
            cliente, che e' esattamente l'idea sbagliata. */}
        <div className="flex flex-col gap-1.5">
          <SedeAutocomplete
            label="Posizione fisica"
            value={sedeFisicaTesto}
            onChange={setSedeFisicaTesto}
            sedeId={sedeFisicaId}
            onSedeIdChange={setSedeFisicaId}
            soloSelezione
            placeholder="Cerca la sede dove si trova la cassaforte…"
          />
          <p className="text-xs text-ink/45">
            Una sola per device, anche quando la cassaforte è condivisa: i clienti che ci versano si aggiungono e si
            tolgono senza che il pezzo si sposti. Lasciarla vuota è legittimo — vuol dire che la posizione non è nota.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="dev-da-evo" className="text-sm font-medium text-ink">
              Installato da Evo
            </label>
            <select
              id="dev-da-evo"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("installato_da_evo")}
            >
              {/* Tre valori e non una spunta: "non noto" è diverso da "no". */}
              <option value="">Non noto</option>
              <option value="si">Sì</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="dev-centro" className="text-sm font-medium text-ink">
              Centro che ha installato
            </label>
            <select
              id="dev-centro"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              {...register("centro_assistenza_installazione_id")}
            >
              <option value="">Non noto</option>
              {centri.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink/45">
              Chi ha montato il device, non chi lo assiste oggi. Se esiste un&apos;installazione collegata nel
              calendario, il dettaglio mostra il centro di quella.
            </p>
          </div>
        </div>

        <div>
          <DispositivoAutocomplete
            label="Modello a catalogo"
            value={catalogoTesto}
            onChange={(v) => {
              setCatalogoTesto(v);
              // Testo modificato a mano senza scegliere dai suggerimenti: il
              // collegamento precedente non vale più.
              if (!v.trim()) setCatalogoId(null);
            }}
            onSelect={(d) => {
              setCatalogoTesto(`${d.marca} ${d.modello}`);
              setCatalogoId(d.id);
            }}
            placeholder="Cerca marca o modello…"
          />
          <p className="mt-1 text-xs text-ink/45">
            {device.modello_raw
              ? `Modello dichiarato nella fonte: "${device.modello_raw}". Collegarlo a un modello del Catalogo non lo cancella.`
              : "Collega il device a un modello del Catalogo Dispositivi per averne schede tecniche e misure."}
          </p>
        </div>

        {correnti.length > 0 && (
          <div className="flex flex-col gap-4">
            <p className="text-sm font-medium text-ink">
              Collocazione e servizio
              {correnti.length > 1 && <span className="font-normal text-ink/50"> — un blocco per cliente</span>}
            </p>

            {correnti.map((rel) => {
              const campi = relazioni[rel.id];
              if (!campi) return null;
              const sedi = sediPerCliente[rel.cliente.id];

              return (
                <div key={rel.id} className="flex flex-col gap-3 rounded-lg border border-line bg-line/10 p-3">
                  {correnti.length > 1 && (
                    <p className="text-xs font-semibold text-ink/60">{rel.cliente.nome}</p>
                  )}
                  <CampiDellaRelazione
                    nomeCliente={rel.cliente.nome}
                    campi={campi}
                    sedi={sedi}
                    operatori={operatori}
                    eCassaforte={eCassaforte}
                    notaGdo={notaGdo}
                    onChange={(patch) => aggiornaRelazione(rel.id, patch)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* --- Cliente in più ---------------------------------------------
            Una cassaforte può servire più punti vendita insieme: qui se ne
            aggiunge uno, senza toccare quelli che ci sono già. Spostare il
            device altrove è un'altra cosa — lì si chiude la relazione vecchia
            e se ne apre una nuova — e resta fuori da questo form. */}
        {nuovo === null ? (
          <button
            type="button"
            onClick={() =>
              setNuovo({
                clienteTesto: "",
                cliente_id: "",
                cliente_nome: "",
                sede_id: "",
                nuovaSede: null,
                // Il cliente in più eredita il codice della cassaforte, se ce
                // n'è uno solo: è quello che farebbe comunque il backend.
                codice_gdo: codiciNelForm.length === 1 ? codiciNelForm[0] : "",
                servizio: "",
                conto_tecnico: "",
                trasporto_valori_id: "",
                sala_conta_id: "",
              })
            }
            className="flex items-center gap-1.5 self-start text-sm font-semibold text-primary hover:underline"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Aggiungi cliente a questa cassaforte
          </button>
        ) : (
          <div className="flex flex-col gap-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-semibold text-ink/60">Nuovo cliente</p>
              <button
                type="button"
                onClick={() => setNuovo(null)}
                className="text-xs text-ink/50 hover:text-ink hover:underline"
              >
                Annulla
              </button>
            </div>

            <div className="flex flex-col gap-1.5">
              <ClienteAutocomplete
                label="Cliente"
                value={nuovo.clienteTesto}
                // Scrivere a mano non basta: serve un id vero, e lo dà solo la
                // scelta di un suggerimento. Digitando si azzera la selezione
                // precedente, altrimenti resterebbe agganciato un cliente che
                // nel campo non si legge più.
                onChange={(v) => setNuovo((n) => (n ? { ...n, clienteTesto: v, cliente_id: "", cliente_nome: "" } : n))}
                onSelect={scegliCliente}
              />
              {!nuovo.cliente_id && (
                <p className="text-xs text-ink/45">Scegli un cliente dai suggerimenti per collegarlo.</p>
              )}
            </div>

            {/* Sede, GDO e servizio compaiono solo a cliente scelto: senza, la
                select delle sedi non saprebbe di chi sono. */}
            {nuovo.cliente_id && (
              <CampiDellaRelazione
                nomeCliente={nuovo.cliente_nome}
                campi={nuovo}
                sedi={sediPerCliente[nuovo.cliente_id]}
                operatori={operatori}
                eCassaforte={eCassaforte}
                notaGdo={notaGdo}
                onChange={({ codice_gdo, ...resto }) => {
                  setNuovo((n) => (n ? { ...n, ...resto } : n));
                  if (codice_gdo !== undefined) cambiaCodiceGdo(codice_gdo);
                }}
              />
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="dev-note" className="text-sm font-medium text-ink">
            Note
          </label>
          <textarea
            id="dev-note"
            rows={3}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("note")}
          />
        </div>
      </form>
    </Modal>
  );
}
